import { computed, Injectable, inject, signal } from '@angular/core';
import { catchError, delay, map, Observable, of, tap, throwError } from 'rxjs';
import { UsersService as ApiUsersService } from '../../../api/api/users.service';
import { InfinityPaginationUserResponseDto } from '../../../api/model/infinityPaginationUserResponseDto';
import { User as ApiUser } from '../../../api/model/user';
import { User, UserRole } from '../models/user.model';

interface CreateUserData {
  username: string;
  email: string;
  name: string;
  role: UserRole;
  password: string;
  avatar?: string;
  contractorProfile?: import('../models/user.model').ContractorProfile;
  company?: string;
}

interface UpdateUserData {
  username?: string;
  email?: string;
  name?: string;
  role?: UserRole;
  avatar?: string;
  contractorProfile?: import('../models/user.model').ContractorProfile;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly STORAGE_KEY = 'scalex_users';
  private readonly USERS_STORAGE_KEY = 'scalex_user_credentials';
  private readonly AUTH_STORAGE_KEY = 'scalex_auth';

  // Users state using signals
  private readonly usersSignal = signal<User[]>([]);

  // Computed signals for reactive state
  readonly users = this.usersSignal.asReadonly();
  readonly consultants = computed(() =>
    this.usersSignal().filter((u) => u.role === UserRole.CONSULTANT)
  );
  readonly clients = computed(() => this.usersSignal().filter((u) => u.role === UserRole.CLIENT));
  readonly contractors = computed(() =>
    this.usersSignal().filter((u) => u.role === UserRole.CONTRACTOR)
  );
  readonly admins = computed(() =>
    this.usersSignal().filter((u) => u.role === UserRole.SUPER_ADMIN)
  );
  readonly usersCount = computed(() => this.usersSignal().length);

  private readonly apiUsersService = inject(ApiUsersService);

  constructor() {
    this.refreshUsers();
  }

  /**
   * Refresh users list from API
   */
  refreshUsers(): void {
    this.getAllUsers().subscribe();
  }

  /**
   * Get user by ID
   */
  getUserById(id: string): User | undefined {
    return this.usersSignal().find((u) => u.id === id);
  }

  /**
   * Get all users
   */
  getAllUsers(): Observable<User[]> {
    return this.apiUsersService.usersControllerFindAllV1().pipe(
      map((response: InfinityPaginationUserResponseDto) => {
        const users = response.data.map((u) => this.mapApiUserToCoreUser(u));
        this.usersSignal.set(users);
        return users;
      }),
      catchError((error) => {
        console.error('Error fetching users:', error);
        return of([]);
      })
    );
  }

  /**
   * Create a new user
   */
  createUser(userData: CreateUserData): Observable<User> {
    // Split name into firstName and lastName for backend
    const nameParts = (userData.name || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '.';

    const apiPayload: any = {
      ...userData,
      firstName,
      lastName,
      appRole: userData.role // Map frontend 'role' to backend 'appRole'
    };

    return this.apiUsersService.usersControllerCreateV1(apiPayload).pipe(
      map((apiUser: ApiUser) => {
        const newUser = this.mapApiUserToCoreUser(apiUser);
        this.usersSignal.update((users) => [...users, newUser]);
        return newUser;
      })
    );
  }

  /**
   * Update existing user
   */
  updateUser(id: string, updates: UpdateUserData): Observable<User> {
    const { role, ...otherUpdates } = updates;
    const apiPayload: any = {
      ...otherUpdates,
      appRole: role
    };

    return this.apiUsersService.usersControllerUpdateV1(id, apiPayload).pipe(
      map((apiUser: ApiUser) => {
        const updatedUser = this.mapApiUserToCoreUser(apiUser);
        this.usersSignal.update((users) => users.map((u) => (u.id === id ? updatedUser : u)));
        return updatedUser;
      })
    );
  }

  /**
   * Delete user
   */
  deleteUser(id: string): Observable<boolean> {
    return this.apiUsersService.usersControllerRemoveV1(id).pipe(
      map(() => {
        this.usersSignal.update((users) => users.filter((u) => u.id !== id));
        return true;
      })
    );
  }

  /**
   * Search users by query
   */
  searchUsers(query: string): User[] {
    if (!query.trim()) {
      return this.usersSignal();
    }

    const lowerQuery = query.toLowerCase();
    return this.usersSignal().filter(
      (user) =>
        user.name.toLowerCase().includes(lowerQuery) ||
        user.email.toLowerCase().includes(lowerQuery) ||
        user.username.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Filter users by role
   */
  filterUsersByRole(role: UserRole | null): User[] {
    if (!role) {
      return this.usersSignal();
    }
    return this.usersSignal().filter((u) => u.role === role);
  }

  /**
   * Reset user password (for Super Admin)
   */
  /**
   * Reset user password (for Super Admin)
   */
  resetUserPassword(userId: string, newPassword: string): Observable<boolean> {
    console.warn('Password reset via API not yet implemented');
    return of(true).pipe(delay(500));
  }

  private mapApiUserToCoreUser(apiUser: ApiUser): User {
    return {
      id: apiUser.id.toString(),
      username: apiUser.username,
      email: apiUser.email,
      role: apiUser.appRole as unknown as UserRole,
      name: apiUser.name || `${apiUser.firstName} ${apiUser.lastName}`,
      avatar: apiUser.avatar,
      // Map other fields if necessary
      contractorProfile: apiUser.contractorProfile
        ? {
            // Map contractor profile if needed, or cast it if structure matches
            ...(apiUser.contractorProfile as any)
          }
        : undefined
    };
  }
}
